import { z } from 'zod'

// Section types that can appear in a report (camelCase to match Kysely CamelCasePlugin)
export const reportSectionType = z.enum([
  // Static text sections - content stored at different levels
  'templateStaticText', // Content stored in template.config, unchangeable per instance
  'configurationStaticText', // Content stored in configuration.config (e.g., Service Manager Summary)
  'instanceStaticText', // Placeholder for per-instance content added in Word (e.g., Executive Summary)

  // TFT data-driven sections
  'annotatedMap', // User-uploaded map image with positioned labels (for TFT opening dates)
  'siteMap', // Map showing site locations
  'temperatureProfile', // Chart: depth vs temperature
  'temperatureTable', // Table: site name, transmit time, temps at depths
  // TFT sections requiring external integration (future)
  'forecastComparison', // Forecast vs actual ground temperature

  // WIV sections
  'wivExecutiveSummary', // Title page with top sites table and manual narrative
  'wivPeakCorrelationsTable', // SO vs SW peak amplitude correlations table
  'wivSoSwScatterChart', // SO vs SW scatter plot with trendline
  'wivSensorSettings', // Sensor configuration settings table
  'wivPeakAmplitudeChart', // Peak amplitude bar chart (all sites)
  'wivWindCorrelationCharts', // Wind angle and speed scatter charts
  'wivWindAngleTable', // Detailed wind angle at peak amplitude table
  'wivThresholdsTable', // Time over amplitude thresholds table
  'wivAmplitudeHoursChart', // Bar charts for hours over each threshold
  'wivTopSitesTimeSeries', // Time series charts for top N sites
  'wivWindStationData', // Wind rose and wind time series per station
])

export type ReportSectionType = z.infer<typeof reportSectionType>

// Section configuration
export const reportSection = z.object({
  id: z.string(), // Unique section identifier
  type: reportSectionType,
  title: z.string(),
  order: z.number(), // Display order
  enabled: z.boolean().default(true),
  pageBreakBefore: z.boolean().default(false), // Insert page break before this section
  content: z.string().optional(), // For static text sections
  config: z.record(z.string(), z.any()).optional(), // Section-specific config
})

export type ReportSection = z.infer<typeof reportSection>

// Full report template configuration
export const reportTemplateConfig = z.object({
  sections: z.array(reportSection),
  styling: z
    .object({
      fontFamily: z.string().default('Lato'),
      fontSize: z.number().default(11),
      headingColor: z.string().default('rgb(68, 114, 196)'),
      pageOrientation: z.enum(['portrait', 'landscape']).default('portrait'),
    })
    .optional(),
  metadata: z
    .object({
      includePageNumbers: z.boolean().default(true),
      includeFooter: z.boolean().default(true),
      footerText: z.string().optional(),
    })
    .optional(),
})

export type ReportTemplateConfig = z.infer<typeof reportTemplateConfig>
